import { forkJoin, from, map, of, switchMap } from 'rxjs'
import { create } from 'zustand'
import api from '../api'
import { getSessionFromUser } from '../api/brm/authentication'
import { client_id as demoClientId, initDemo } from '../api/demo'
import { connectWebSocket } from '../api/expurgate-notifications'
import { joinChannel, leaveChannel } from '../broadcast'
import i18n from '../i18n'

const initialUserState = {
  client: null,
  clients: [],
  datasources: [],
  session: null,
  settings: null,
  theme: null,
  view_as_role: null
}

export function createSession$(nextUser) {
  connectWebSocket()
  joinChannel()

  const session = getSessionFromUser(nextUser)

  return forkJoin([
    of(session),
    api.usersettings.find$(undefined, { id: session.sub.id }),
    api.datasources.findAll$(),
    api.clients.findAll$()
  ]).pipe(
    switchMap(
      ([
        session,
        {
          data: [settings]
        },
        { data: datasources },
        { data: clients }
      ]) => {
        if (settings.language && settings.language !== i18n.language) {
          i18n.changeLanguage(settings.language)
        }

        const client = clients.find((c) => c.client_id === settings.client_id)
        const nextUser = { client, clients, datasources, session, settings }

        const joins = [of(nextUser)]

        if (!session.sub.superadmin && settings.client_id === demoClientId) {
          window.isDemo = true
          joins.push(from(initDemo(nextUser)))
        }

        return forkJoin(joins)
      }
    )
  )
}

export function getCustomSettings$(key, id, extraOpts) {
  id ??= store.getState().user.session.sub.id
  return api.usersettings
    .find$({ limit: null, skip: null, ...extraOpts }, { id })
    .pipe(
      switchMap(({ data: [{ custom }] }) => {
        custom = !custom || Array.isArray(custom) ? {} : custom
        return of(key ? custom[key] : custom)
      })
    )
}

export function handleLogout$() {
  return api.authentication.logout$().pipe(
    map((res) => {
      leaveChannel()
      api.expurgatealerts.disconnect()
      store.setState({ user: initialUserState })
      return res
    })
  )
}

export function handleSession$(u) {
  return createSession$(u).pipe(
    map(([nextUser]) => {
      store.setState({ user: nextUser })
      return nextUser
    })
  )
}

export function saveCustomSettings$(newSettings = {}, id, extraOpts) {
  const u = store.getState().user
  id ??= u.session.sub.id

  return getCustomSettings$(undefined, id, extraOpts).pipe(
    switchMap((custom) => {
      const nextState = {
        ...custom,
        ...newSettings
      }

      store.setState({
        user: {
          ...u,
          settings: {
            ...u.settings,
            custom: nextState
          }
        }
      })

      return api.usersettings.create$({ custom: nextState }, { id }, extraOpts)
    })
  )
}

export function saveSettings$(newSettings, id, extraOpts) {
  const u = store.getState().user
  id ??= u.session?.sub.id

  if (u.session) {
    return api.usersettings.create$(newSettings, { id }, extraOpts).pipe(
      switchMap(() =>
        // create$() does not return any permissions, so we need a
        // subsequent find$()
        api.usersettings.find$({ limit: null, skip: null }, { id })
      ),
      map(({ data: [settings] }) => {
        store.setState({ user: { ...u, settings } })
        return settings
      })
    )
  }

  return of(newSettings)
}

const store = create((_, get) => ({
  loading: 0,
  openSidebar: false,
  user: initialUserState,

  hasAdmin: () => {
    const u = get().user
    return (
      (u.session?.sub.superadmin && !u.view_as_role) ||
      u.session?.sub.administrator ||
      u.settings?.roles.includes('administrator')
    )
  },

  hasPermissions: (perms, needsAllPerms) => {
    needsAllPerms ??= true
    const u = get().user

    if (
      !perms ||
      !u.settings?.permissions ||
      (u.session?.sub.superadmin && !u.view_as_role)
    ) {
      return true
    }

    if (typeof perms === 'string') {
      perms = [perms]
    }

    const userPerms = Object.entries(
      u.view_as_role || u.settings?.permissions || {}
    ).flatMap(([endpoint, verbs]) => verbs.map((verb) => `${endpoint}:${verb}`))

    return needsAllPerms
      ? perms.every((p) => userPerms.includes(p))
      : perms.some((p) => userPerms.includes(p))
  },

  hasSuperadmin: () => {
    const u = get().user
    return u.session?.sub.superadmin && !u.view_as_role
  },

  setDatasources: (datasources) => {
    const u = get().user
    store.setState({ user: { ...u, datasources } })
  },

  setClient: (client) => {
    const u = get().user
    store.setState({ user: { ...u, client } })
  },

  setClients: (clients) => {
    const u = get().user
    store.setState({ user: { ...u, clients } })
  },

  setViewAsRole(role) {
    const u = get().user
    store.setState({ user: { ...u, view_as_role: role } })
  }
}))

export function decrLoading() {
  store.setState({ loading: --store.getState().loading })
}

export function incrLoading() {
  store.setState({ loading: ++store.getState().loading })
}

export function toggleSidebar(nextState) {
  store.setState({ openSidebar: nextState ?? !store.getState().openSidebar })
}

export default store
